import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LevelModel } from '../types/protocols';
import { levelDisplayName } from '../Helpers/levelDisplayName';
import { ROOF_PLAN_LABEL } from '../Helpers/roofPlan';
import { SATELLITE_LABEL } from '../Helpers/mapLocations';
import './LevelNavigator.css';

type NavigableLevel = Pick<LevelModel, 'key' | 'name' | 'number'>;

interface LevelNavigatorProps {
    /** Levels to offer; order is always ascending by number. */
    levels: NavigableLevel[];
    selectedKey?: string;
    onSelect?: (levelKey: string) => void;
    /** When given, the level pills are links to this path (Cmd/Ctrl-click opens a new tab) instead of calling `onSelect`. */
    levelPath?: (levelKey: string) => string;
    /** Optional per-level count, shown as a " (n)" suffix when above zero. */
    getCount?: (levelKey: string) => number;
    /**
     * A "Takplan" pill after the levels. The takplan is a route (the roof
     * page), not a level: the caller renders it only when the building has
     * roof components or a roof drawing (D18), and only where something can
     * sit on the roof.
     */
    roofPlan?: {
        selected?: boolean;
        onSelect?: () => void;
        /** The roof page's path; the pill is then a link. */
        to?: string;
    };
    /** A "Satellit" pill before the levels, for pickers that also look at the satellite image. */
    satellite?: {
        selected?: boolean;
        onSelect: () => void;
    };
    className?: string;
}

/** Pill row for switching between a building's levels (and the takplan, i.e. the roof page). Used by the level, units and storage pages. */
const LevelNavigator: React.FC<LevelNavigatorProps> = ({ levels, selectedKey, onSelect, levelPath, getCount, roofPlan, satellite, className }) => {
    const sorted = useMemo(
        () => [...levels].sort((a, b) => a.number - b.number || a.key.localeCompare(b.key)),
        [levels]
    );

    if (sorted.length === 0 && !roofPlan && !satellite) return null;

    return (
        <div className={`level-buttons-container${className ? ` ${className}` : ''}`} role="group" aria-label="Våningsplan">
            {satellite && (
                <button
                    type="button"
                    className={`level-button ${satellite.selected ? 'level-button--current' : ''}`}
                    onClick={satellite.selected ? undefined : satellite.onSelect}
                    disabled={satellite.selected}
                    aria-current={satellite.selected ? 'true' : undefined}
                >
                    {SATELLITE_LABEL}
                </button>
            )}
            {sorted.map(level => {
                const isCurrent = level.key === selectedKey;
                const count = getCount?.(level.key) ?? 0;
                const label = <>{levelDisplayName(level)}{count > 0 ? ` (${count})` : ''}</>;
                if (levelPath && !isCurrent) {
                    return (
                        <Link key={level.key} to={levelPath(level.key)} className="level-button">
                            {label}
                        </Link>
                    );
                }
                return (
                    <button
                        key={level.key}
                        type="button"
                        className={`level-button ${isCurrent ? 'level-button--current' : ''}`}
                        onClick={isCurrent ? undefined : () => onSelect?.(level.key)}
                        disabled={isCurrent}
                        aria-current={isCurrent ? 'true' : undefined}
                    >
                        {label}
                    </button>
                );
            })}
            {roofPlan && roofPlan.to && !roofPlan.selected && (
                <Link to={roofPlan.to} className="level-button">
                    {ROOF_PLAN_LABEL}
                </Link>
            )}
            {roofPlan && !(roofPlan.to && !roofPlan.selected) && (
                <button
                    type="button"
                    className={`level-button ${roofPlan.selected ? 'level-button--current' : ''}`}
                    onClick={roofPlan.selected ? undefined : roofPlan.onSelect}
                    disabled={roofPlan.selected}
                    aria-current={roofPlan.selected ? 'true' : undefined}
                >
                    {ROOF_PLAN_LABEL}
                </button>
            )}
        </div>
    );
};

export default LevelNavigator;
